import React from "react";
import type { ReactNode } from "react";
import { Route } from "react-router-dom";
import type { RouteComponentProps, RouteProps } from "react-router-dom";

import { showToast } from "@/providers/ToastProvider";
import { Spinner2 } from "@/components/UI2/atoms/Spinner";

import { ACCESS_DENIED_ROLE_MESSAGE } from "../constants/messages";
import type { PermissionKey } from "../types";
import { useCheckPermission } from "../hooks/useCheckPermission";

type ProtectedRouteProps = {
  children: ReactNode | ((props: RouteComponentProps) => ReactNode);
  deniedMessage?: string;
  fallbackRoute?: string;
  permissionKey: PermissionKey;
} & Omit<RouteProps, "component" | "render" | "children">;

export function ProtectedRoute({
  children,
  deniedMessage = ACCESS_DENIED_ROLE_MESSAGE,
  fallbackRoute = "/app/home",
  permissionKey,
  ...routeProps
}: ProtectedRouteProps) {
  const hasPermission = useCheckPermission({ permissionKey });

  // Using history.replace instead of <Redirect> to avoid render loops
  return (
    <Route
      {...routeProps}
      render={(props) => {
        // Show spinner while role/profile are loading
        if (hasPermission === null) {
          return (
            <div className="tw:flex tw:min-h-100 tw:items-center tw:justify-center">
              <Spinner2 />
            </div>
          );
        }

        const isFallbackRoute = props.location.pathname === fallbackRoute;

        if (!hasPermission && !isFallbackRoute) {
          props.history.replace(fallbackRoute);
          showToast([
            {
              heading: "Access Denied",
              text: deniedMessage,
              variant: "error",
            },
          ]);
        }

        if (!hasPermission) return null;

        return typeof children === "function" ? children(props) : children;
      }}
    />
  );
}
